'use client';

import { forwardRef } from 'react';

import { IconInfoCircle } from '@tabler/icons-react';
import clsx from 'clsx';

import { Tooltip } from '../Tooltip';

interface ITextInput {
  id?: string;
  label?: string;
  required?: boolean;
  inputPlaceHolder?: string;
  autoFocus?: boolean;
  rightSection?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  enablePasswordManagerAutofill?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  defaultValue?: string | null;
  readOnly?: boolean;
  errorMessage?: string | null;
  type?: 'text' | 'email' | 'password';
  tooltip?: string;
  minLength?: number;
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  inputMode?: 'text' | 'email' | 'search' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal' | undefined;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
  className?: string;
  enablePointerCursor?: boolean;
}

export const TextInput = forwardRef<HTMLInputElement, ITextInput>(
  (
    {
      id,
      label,
      required = false,
      inputPlaceHolder,
      autoFocus = false,
      rightSection,
      inputMode,
      onChange,
      onBlur,
      enablePasswordManagerAutofill = false,
      dataTestId,
      disabled = false,
      defaultValue,
      readOnly = false,
      onKeyDown,
      tooltip,
      errorMessage,
      type = 'text',
      minLength,
      maxLength,
      min,
      max,
      enablePointerCursor = false,
      ...props
    },
    ref,
  ) => (
    <div className="flex w-full flex-row gap-2 md:flex-col">
      {label && (
        <div className="flex gap-1">
          {tooltip ? (
            <Tooltip withArrow label={tooltip}>
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-black" htmlFor={id ?? label}>
                  {label}
                </label>
                <IconInfoCircle className="size-4" />
              </div>
            </Tooltip>
          ) : (
            <label className="text-sm font-medium text-black" htmlFor={id ?? label}>
              {label}
            </label>
          )}
          {required && <span className="text-accent-red">*</span>}
        </div>
      )}
      <div className="relative w-full">
        <input
          id={id ?? label}
          name={id ?? label}
          data-testid={dataTestId}
          type={type}
          className={clsx(
            'h-9 w-full rounded-md border pl-4 pr-9 text-sm text-black transition-colors duration-300 ease-in-out placeholder:text-gray-600 focus:border-primary focus:outline-none focus:ring-0 focus:ring-primary disabled:text-gray-600',
            disabled ? 'cursor-not-allowed' : enablePointerCursor && 'cursor-pointer',
            errorMessage
              ? 'border-accent-red !text-accent-red placeholder:text-accent-red focus:border-accent-red'
              : 'border-gray-100',
          )}
          placeholder={inputPlaceHolder}
          autoFocus={autoFocus}
          onChange={onChange && onChange}
          onBlur={onBlur && onBlur}
          disabled={disabled}
          readOnly={readOnly}
          onKeyDown={onKeyDown}
          // @ts-expect-error we only render defaultValue when its provided
          defaultValue={defaultValue}
          maxLength={maxLength}
          minLength={minLength}
          min={min}
          max={max}
          inputMode={inputMode}
          autoComplete={enablePasswordManagerAutofill ? undefined : 'off'}
          data-lpignore={enablePasswordManagerAutofill ? undefined : 'true'} // lastpass
          data-1p-ignore={enablePasswordManagerAutofill ? undefined : 'true'} // 1Password
          ref={ref}
          {...props}
        />
        <div className="absolute inset-y-0 right-0 flex h-9 items-center justify-center px-2">
          {rightSection && rightSection}
        </div>
        {errorMessage && <span className="text-xs text-accent-red">{errorMessage}</span>}
      </div>
    </div>
  ),
);

TextInput.displayName = 'TextInput';
